import React, {ReactNode, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {CenterBox} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {useScrollableListItemTracking} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {IRPStore} from "../../../app/rp_reducer";
import {PropertyItem} from "../../../atoms/PropertyItem";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {PropertyBoxType} from "../../../property/types/PropertyBoxType";
import {getRecommendationFromProperty} from "../../../recommendations/utils/get_recommendation_from_property";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {checkHasVerifiedPhone} from "../../../user/utils/check_has_verified_phone";
import {ViewType} from "../../../view_type/ViewType";
import {hitMultileadRecommendationShown} from "../../tracking/lead/hit_multilead_recommendation_shown";
import {MultileadVersion} from "../../types/lead/MultileadVersion";
import {RecommendationSource} from "../../types/lead/RecommendationSource";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadSimpleRecommendationsMap} from "./ApplicationMultiLeadSimpleRecommendationsMap";

interface IProps {
    listTopSlot?: ReactNode;
    mainPropertyId?: IPropertyDetail["id"];
    allowToSelectMainProperty?: boolean;
    recommendedProperties: IRecommendedProperty[];
    onListScroll: (event: React.UIEvent) => void;
    isScrolledToBottom: boolean;
    selectedPropertyIds: number[];
    handleChange: (viewType: ViewType, propertyId: number) => void;
    originalApplicationGeoPoint: {coordinates: [number, number]} | null | undefined;
    originalApplicationCountry?: Country;
    originalApplicationEstimatedPrice?: string | null;
    handlePinClick: (viewType: ViewType | null, recommendedItemID: number) => void;
}

export const ApplicationMultiLeadSimpleRecommendationsPropertyList = (props: IProps) => {
    const [hoveredItemID, setHoveredItemID] = useState<null | number>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const listWrap = useRef<HTMLUListElement>(null);
    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const {isMobile} = useUserDevice();
    const {data: userProfile} = useGetUserProfileQuery();
    const hasVerifiedPhone = checkHasVerifiedPhone(userProfile);

    const allowToSelectMainProperty = !!props.allowToSelectMainProperty;
    const {data: mainPropertyDetail, isLoading: isLoadingMainPropertyDetail} = useGetPropertyDetailQuery(props.mainPropertyId!, {
        skip: !props.mainPropertyId
    });

    const ignoredCn = useScrollableListItemTracking({
        scrollableListRef: listRef,
        directAncestorListRef: listWrap,
        listElementTrackingIdDatasetName: "trackId",
        handleListItemTrackingHit: (trackingId) => {
            hitMultileadRecommendationShown({
                recommendationId: trackingId,
                recommendationType: ApplicationMultiLeadRecommendationType.PROPERTY,
                recommendationSource: RecommendationSource.DISTANCE,
                multileadVersion: MultileadVersion.RP_LIST_PROPERTY,
                viewType
            });
        },
        onError: (error) => {
            console.warn(error);
            notifyBugsnag(new Error(error), error);
        }
    });

    if (isLoadingMainPropertyDetail) {
        return (
            <CenterBox>
                <Loader size="md" />
            </CenterBox>
        );
    }

    const onListItemHover = (itemID: number | null) => {
        setHoveredItemID(itemID);
    };

    return (
        <div>
            <div className={modalWrapperStyle}>
                <AdvancedScroll
                    ref={listRef}
                    height={440}
                    contentClassName={leftSectionScrollContentStyle}
                    scrollTopShadowClassName={contentTopShadowStyle}
                    scrollBottomShadowClassName={contentBottomShadowStyle}
                    disableScroll={!!isMobile}
                    className={cx(leftSectionWrapperStyle, fullWidthStyle)}
                >
                    <ul onScroll={props.onListScroll} ref={listWrap} className={listWrapperStyle}>
                        <div className={ignoredCn}>{props.listTopSlot}</div>
                        {allowToSelectMainProperty && mainPropertyDetail ? (
                            <PropertyItem
                                data-track-id={mainPropertyDetail.id}
                                property={getRecommendationFromProperty(mainPropertyDetail)}
                                checked={props.selectedPropertyIds.includes(mainPropertyDetail.id)}
                                onHover={onListItemHover}
                                handleChange={() => props.handleChange(ViewType.PROPERTY, mainPropertyDetail.id)}
                                type={PropertyBoxType.MAIN}
                                hidePrices={!hasVerifiedPhone}
                            />
                        ) : null}

                        {[...props.recommendedProperties]
                            .sort((a, b) => (a.price || 0) - (b.price || 0))
                            .map((recommendedItem) => {
                                const isChecked = props.selectedPropertyIds.includes(recommendedItem.id);
                                const hasPriceComparison = hasVerifiedPhone && !!recommendedItem.price && !!mainPropertyDetail?.price;
                                const hasAreaComparison = !!mainPropertyDetail?.area && !!recommendedItem.area;

                                const recommendationAdvantages =
                                    hasPriceComparison || hasAreaComparison
                                        ? {
                                              priceDifference: hasPriceComparison ? mainPropertyDetail.price! - recommendedItem.price! : 0,
                                              areaDifference: hasAreaComparison ? mainPropertyDetail.area - recommendedItem.area : 0
                                          }
                                        : undefined;

                                return (
                                    <PropertyItem
                                        key={recommendedItem.id}
                                        recommendationAdvantages={recommendationAdvantages}
                                        data-track-id={recommendedItem.id}
                                        property={recommendedItem}
                                        checked={isChecked}
                                        onHover={onListItemHover}
                                        handleChange={() => props.handleChange(ViewType.PROPERTY, recommendedItem.id)}
                                        type={PropertyBoxType.RECOMMENDED}
                                        hidePrices={!hasVerifiedPhone}
                                    />
                                );
                            })}
                    </ul>
                </AdvancedScroll>

                {props.originalApplicationGeoPoint && props.originalApplicationCountry && (
                    <div className={mapWrapperStyle}>
                        <ApplicationMultiLeadSimpleRecommendationsMap
                            hoveredItemID={hoveredItemID}
                            originalGeoPoint={props.originalApplicationGeoPoint!}
                            originalCountry={props.originalApplicationCountry!}
                            originalOfferLabel={props.originalApplicationEstimatedPrice}
                            viewType={viewType}
                            handlePinClick={props.handlePinClick}
                            recommendations={props.recommendedProperties}
                            recommendationType={ApplicationMultiLeadRecommendationType.PROPERTY}
                            checkedList={props.selectedPropertyIds}
                            className={mapStyle}
                            hidePrices={!hasVerifiedPhone}
                            mainPropertyId={allowToSelectMainProperty ? props.mainPropertyId : undefined}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export const listWrapperStyle = css({
    margin: 0,
    padding: 0,
    listStyle: "none",
    position: "relative",
    flexBasis: "calc(50% - 4.8rem)",
    sm: {
        pr: 5,
        pb: 2,
        maxHeight: "inherit"
    }
});

const leftSectionWrapperStyle = css({
    marginBottom: 2,
    sm: {
        marginBottom: 0,
        pb: 2
    }
});

const fullWidthStyle = css({
    width: "100%"
});

const modalWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    marginBottom: 2,
    sm: {
        justifyContent: "space-between",
        gap: "0.8rem",
        flexDirection: "row"
    }
});

const mapWrapperStyle = css({
    marginBottom: 5,
    position: "relative",
    width: "100%",
    sm: {
        marginBottom: 0,
        height: "440px",
        flex: "0 0 50%"
    }
});

const mapStyle = css({
    sm: {
        overflow: "hidden",
        borderRadius: 1
    }
});

const leftSectionScrollContentStyle = css({
    pl: 1.5,
    md: {
        pl: 0
    }
});

const contentTopShadowStyle = css({
    background: "linear-gradient(rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))"
});

const contentBottomShadowStyle = css({
    background: "linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))"
});
import React, {ReactNode, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {CenterBox} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {useScrollableListItemTracking} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {IRPStore} from "../../../app/rp_reducer";
import {PropertyItem} from "../../../atoms/PropertyItem";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {useGetPropertyDetailQuery} from "../../../property/api/get_property_detail";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {PropertyBoxType} from "../../../property/types/PropertyBoxType";
import {getRecommendationFromProperty} from "../../../recommendations/utils/get_recommendation_from_property";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {checkHasVerifiedPhone} from "../../../user/utils/check_has_verified_phone";
import {ViewType} from "../../../view_type/ViewType";
import {hitMultileadRecommendationShown} from "../../tracking/lead/hit_multilead_recommendation_shown";
import {MultileadVersion} from "../../types/lead/MultileadVersion";
import {RecommendationSource} from "../../types/lead/RecommendationSource";
import {ApplicationMultiLeadRecommendationType} from "../../types/multi_lead/ApplicationMultiLeadRecommendationType";
import {ApplicationMultiLeadSimpleRecommendationsMap} from "./ApplicationMultiLeadSimpleRecommendationsMap";

interface IProps {
    listTopSlot?: ReactNode;
    mainPropertyId?: IPropertyDetail["id"];
    allowToSelectMainProperty?: boolean;
    recommendedProperties: IRecommendedProperty[];
    onListScroll: (event: React.UIEvent) => void;
    isScrolledToBottom: boolean;
    selectedPropertyIds: number[];
    handleChange: (viewType: ViewType, propertyId: number) => void;
    originalApplicationGeoPoint: {coordinates: [number, number]} | null | undefined;
    originalApplicationCountry?: Country;
    originalApplicationEstimatedPrice?: string | null;
    handlePinClick: (viewType: ViewType | null, recommendedItemID: number) => void;
}

export const ApplicationMultiLeadSimpleRecommendationsPropertyList = (props: IProps) => {
    const [hoveredItemID, setHoveredItemID] = useState<null | number>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const listWrap = useRef<HTMLUListElement>(null);
    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const {isMobile} = useUserDevice();
    const {data: userProfile} = useGetUserProfileQuery();
    const hasVerifiedPhone = checkHasVerifiedPhone(userProfile);

    const allowToSelectMainProperty = !!props.allowToSelectMainProperty;
    const {data: mainPropertyDetail, isLoading: isLoadingMainPropertyDetail} = useGetPropertyDetailQuery(props.mainPropertyId!, {
        skip: !props.mainPropertyId
    });

    const ignoredCn = useScrollableListItemTracking({
        scrollableListRef: listRef,
        directAncestorListRef: listWrap,
        listElementTrackingIdDatasetName: "trackId",
        handleListItemTrackingHit: (trackingId) => {
            hitMultileadRecommendationShown({
                recommendationId: trackingId,
                recommendationType: ApplicationMultiLeadRecommendationType.PROPERTY,
                recommendationSource: RecommendationSource.DISTANCE,
                multileadVersion: MultileadVersion.RP_LIST_PROPERTY,
                viewType
            });
        },
        onError: (error) => {
            console.warn(error);
            notifyBugsnag(new Error(error), error);
        }
    });

    if (isLoadingMainPropertyDetail) {
        return (
            <CenterBox>
                <Loader size="md" />
            </CenterBox>
        );
    }

    const onListItemHover = (itemID: number | null) => {
        setHoveredItemID(itemID);
    };

    return (
        <div>
            <div className={modalWrapperStyle}>
                <AdvancedScroll
                    ref={listRef}
                    height={440}
                    contentClassName={leftSectionScrollContentStyle}
                    scrollTopShadowClassName={contentTopShadowStyle}
                    scrollBottomShadowClassName={contentBottomShadowStyle}
                    disableScroll={!!isMobile}
                    className={cx(leftSectionWrapperStyle, fullWidthStyle)}
                >
                    <ul onScroll={props.onListScroll} ref={listWrap} className={listWrapperStyle}>
                        <div className={ignoredCn}>{props.listTopSlot}</div>
                        {allowToSelectMainProperty && mainPropertyDetail ? (
                            <PropertyItem
                                data-track-id={mainPropertyDetail.id}
                                property={getRecommendationFromProperty(mainPropertyDetail)}
                                checked={props.selectedPropertyIds.includes(mainPropertyDetail.id)}
                                onHover={onListItemHover}
                                handleChange={() => props.handleChange(ViewType.PROPERTY, mainPropertyDetail.id)}
                                type={PropertyBoxType.MAIN}
                                hidePrices={!hasVerifiedPhone}
                            />
                        ) : null}

                        {[...props.recommendedProperties]
                            .sort((a, b) => (a.price || 0) - (b.price || 0))
                            .map((recommendedItem) => {
                                const isChecked = props.selectedPropertyIds.includes(recommendedItem.id);
                                const hasPriceComparison = hasVerifiedPhone && !!recommendedItem.price && !!mainPropertyDetail?.price;
                                const hasAreaComparison = !!mainPropertyDetail?.area && !!recommendedItem.area;

                                const recommendationAdvantages =
                                    hasPriceComparison || hasAreaComparison
                                        ? {
                                              priceDifference: hasPriceComparison ? mainPropertyDetail.price! - recommendedItem.price! : 0,
                                              areaDifference: hasAreaComparison ? mainPropertyDetail.area - recommendedItem.area : 0
                                          }
                                        : undefined;

                                return (
                                    <PropertyItem
                                        key={recommendedItem.id}
                                        recommendationAdvantages={recommendationAdvantages}
                                        data-track-id={recommendedItem.id}
                                        property={recommendedItem}
                                        checked={isChecked}
                                        onHover={onListItemHover}
                                        handleChange={() => props.handleChange(ViewType.PROPERTY, recommendedItem.id)}
                                        type={PropertyBoxType.RECOMMENDED}
                                        hidePrices={!hasVerifiedPhone}
                                    />
                                );
                            })}
                    </ul>
                </AdvancedScroll>

                {props.originalApplicationGeoPoint && props.originalApplicationCountry && (
                    <div className={mapWrapperStyle}>
                        <ApplicationMultiLeadSimpleRecommendationsMap
                            hoveredItemID={hoveredItemID}
                            originalGeoPoint={props.originalApplicationGeoPoint!}
                            originalCountry={props.originalApplicationCountry!}
                            originalOfferLabel={props.originalApplicationEstimatedPrice}
                            viewType={viewType}
                            handlePinClick={props.handlePinClick}
                            recommendations={props.recommendedProperties}
                            recommendationType={ApplicationMultiLeadRecommendationType.PROPERTY}
                            checkedList={props.selectedPropertyIds}
                            className={mapStyle}
                            hidePrices={!hasVerifiedPhone}
                            mainPropertyId={allowToSelectMainProperty ? props.mainPropertyId : undefined}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export const listWrapperStyle = css({
    margin: 0,
    padding: 0,
    listStyle: "none",
    position: "relative",
    flexBasis: "calc(50% - 4.8rem)",
    sm: {
        pr: 5,
        pb: 2,
        maxHeight: "inherit"
    }
});

const leftSectionWrapperStyle = css({
    marginBottom: 2,
    sm: {
        marginBottom: 0,
        pb: 2
    }
});

const fullWidthStyle = css({
    width: "100%"
});

const modalWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    marginBottom: 2,
    sm: {
        justifyContent: "space-between",
        gap: "0.8rem",
        flexDirection: "row"
    }
});

const mapWrapperStyle = css({
    marginBottom: 5,
    position: "relative",
    width: "100%",
    sm: {
        marginBottom: 0,
        height: "440px",
        flex: "0 0 50%"
    }
});

const mapStyle = css({
    sm: {
        overflow: "hidden",
        borderRadius: 1
    }
});

const leftSectionScrollContentStyle = css({
    pl: 1.5,
    md: {
        pl: 0
    }
});

const contentTopShadowStyle = css({
    background: "linear-gradient(rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))"
});

const contentBottomShadowStyle = css({
    background: "linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))"
});
